import { SearchCriteria, formatDataObject } from '@app-store'
import { SORT_KEYS } from '@ui-kit'
import { ExportPayloadType } from '../types/correspondenceLogs.types'
import {
  ACCESSOR_TO_API_FIELD,
  ACCESSOR_TO_PARENT_ENTITY,
  ExportRelatedEntityConfig,
  ROOT_ENTITY,
  SortDirectionState,
} from '../types/types'

const getApiFieldName = (searchKey: string): string => {
  const accessor = searchKey.toLowerCase()
  return ACCESSOR_TO_API_FIELD[accessor] ?? accessor
}

export const INTEGRATION_LOGS_EXPORT_BASE = {
  entity_name: 'generic_payload',
  select: [
    'status',
    'http_status',
    'request_id',
    'created_at',
    'modified_at',
    'created_by',
    'case',
    'patient',
    'integration_functionality',
  ],
  related_entities: {
    case: {
      select: ['case_key', 'programs', 'products'],
      related_entities: {
        programs: {
          select: ['operational_program_name'],
          ui_data_mappings: {
            operational_program_name: {
              alias: ['Program'],
              queryOrder: [10],
            },
          },
          exclude_fields: ['record_id', 'entity_type', 'category'],
        },
        products: {
          select: ['trademarked_name'],
          ui_data_mappings: {
            trademarked_name: {
              alias: ['Product'],
              queryOrder: [11],
            },
          },
          exclude_fields: ['record_id', 'entity_type', 'category'],
        },
      },
      ui_data_mappings: {
        case_key: {
          alias: ['Case ID'],
          queryOrder: [9],
        },
      },
      exclude_fields: ['record_id', 'entity_type', 'category', 'name'],
    },
    patient: {
      select: ['patient_key'],
      ui_data_mappings: {
        patient_key: {
          alias: ['Entity ID'],
          queryOrder: [8],
        },
      },
      exclude_fields: ['record_id', 'entity_type', 'category', 'name'],
    },
    integration_functionality: {
      select: ['identifier', 'request_type'],
      ui_data_mappings: {
        identifier: {
          alias: ['API Identifier'],
          queryOrder: [1],
        },
        request_type: {
          alias: ['Request Type'],
          queryOrder: [4],
        },
      },
    },
  },
  ui_data_mappings: {
    request_id: {
      alias: ['Transaction ID'],
      queryOrder: [5],
    },
    status: {
      alias: ['Status'],
      queryOrder: [2],
    },
    http_status: {
      alias: ['HTTPS Status Code'],
      queryOrder: [3],
    },
    created_at: {
      alias: ['Request Timestamp'],
      queryOrder: [6],
    },
    modified_at: {
      alias: ['Response Timestamp'],
      queryOrder: [7],
    },
    created_by: {
      alias: ['Triggered By'],
      queryOrder: [12],
    },
  },
  exclude_fields: ['record_id', 'entity_type', 'category'],
} as Omit<ExportPayloadType, 'filters'>

export const buildIntegrationLogsExportPayload = (
  searchCriteria: SearchCriteria[],
  groupId: string,
  sort?: SortDirectionState
): ExportPayloadType => {
  const validCriteria = searchCriteria.filter(
    (criteria) => criteria && Object.keys(criteria).length > 0
  )

  const rootFilters: formatDataObject[] = []
  const relatedFiltersByEntity: Record<string, formatDataObject[]> = {}

  validCriteria.forEach((criteria) => {
    const searchKey = criteria?.search_key ?? ''
    const parentEntity = getParentEntity(searchKey)

    const filter: formatDataObject = {
      operation: criteria?.operator ?? 'EQUALS',
      field_name: getApiFieldName(searchKey),
      values: criteria?.search_value,
    }

    if (parentEntity === ROOT_ENTITY) {
      rootFilters.push(filter)
    } else {
      ;(relatedFiltersByEntity[parentEntity] ??= []).push(filter)
    }
  })

  const isSortActive =
    sort?.direction !== undefined &&
    sort.direction !== SORT_KEYS.Disabled &&
    !!sort.keyName

  const sortEntity = isSortActive ? getParentEntity(sort?.key ?? '') : null

  const sortCriteria: formatDataObject[] = isSortActive
    ? [
        {
          direction: sort?.direction.toUpperCase(),
          field_name: sort?.keyName ?? '',
        },
      ]
    : []

  const buildRelatedFilters = (
    entity: string
  ): Pick<ExportRelatedEntityConfig, 'filters'> => {
    const criteria = relatedFiltersByEntity[entity] ?? []

    const filterBlocks: { criteria: formatDataObject[] }[] = []
    if (criteria.length) filterBlocks.push({ criteria })
    if (sortEntity === entity && sortCriteria.length) {
      filterBlocks.push({ criteria: sortCriteria })
    }

    return filterBlocks.length ? { filters: filterBlocks } : {}
  }

  const filters: object[] = [
    {
      logical_operator: 'AND',
      criteria: [
        ...rootFilters,
        {
          operation: 'EQUALS',
          field_name: 'group_id',
          values: [groupId],
        },
      ],
    },
    {
      criteria: sortEntity === ROOT_ENTITY ? sortCriteria : [],
    },
  ]

  const baseRelated = INTEGRATION_LOGS_EXPORT_BASE.related_entities as Record<
    string,
    ExportRelatedEntityConfig
  >

  const related_entities = {
    ...baseRelated,
    case: {
      ...baseRelated.case,
      ...buildRelatedFilters('case'),
      related_entities: {
        ...baseRelated.case?.related_entities,
        programs: {
          ...baseRelated.case?.related_entities?.programs,
          ...buildRelatedFilters('programs'),
        },
        products: {
          ...baseRelated.case?.related_entities?.products,
          ...buildRelatedFilters('products'),
        },
      },
    },
    integration_functionality: {
      ...baseRelated.integration_functionality,
      ...buildRelatedFilters('integration_functionality'),
    },
    patient: {
      ...baseRelated.patient,
      ...buildRelatedFilters('patient'),
    },
  }

  return {
    ...INTEGRATION_LOGS_EXPORT_BASE,
    related_entities,
    filters,
  } as unknown as ExportPayloadType
}

export const formatJson = (value: Record<string, unknown>): string => {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}
export function getParentEntity(accessor: string): string {
  const key = accessor?.toLowerCase()
  return ACCESSOR_TO_PARENT_ENTITY[key] ?? ROOT_ENTITY
}
export const getProcessingDurationSeconds = (
  requestTimestamp: string | Date | null | undefined,
  responseTimestamp: string | Date | null | undefined
): string => {
  if (!requestTimestamp || !responseTimestamp) return '--'

  const requestMs = new Date(requestTimestamp).getTime()
  const responseMs = new Date(responseTimestamp).getTime()

  if (Number.isNaN(requestMs) || Number.isNaN(responseMs)) return '--'

  const diffMs = responseMs - requestMs
  if (diffMs < 0) return '--'

  return `${(diffMs / 1000).toFixed(2)} s`
}

export const copyToClipboard = async (value: string): Promise<void> => {
  if (!value) return

  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(value)
    return
  }

  const textArea: HTMLTextAreaElement = document.createElement('textarea')
  textArea.value = value
  textArea.style.position = 'fixed'
  textArea.style.opacity = '0'
  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()
  document.execCommand('copy')
  document.body.removeChild(textArea)
}
